import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import SliderInput from '../../components/SliderInput';

const ExponentCalculator = () => {
  const [base, setBase] = useState(2);
  const [exp, setExp] = useState(10);

  const result = useMemo(() => {
    const r = Math.pow(base, exp);
    return r;
  }, [base, exp]);

  const scientific = useMemo(() => {
    if (!isFinite(result) || result === 0) return String(result);
    const absR = Math.abs(result);
    if (absR >= 1e6 || absR < 1e-3) return result.toExponential(6);
    return result.toLocaleString('en-IN', { maximumFractionDigits: 10 });
  }, [result]);

  return (
    <CalculatorLayout
      title="Exponent Calculator"
      subtitle="Raise any base to any power — integers, decimals, and negative exponents."
      category="Math"
      description={<p>An exponent tells how many times to multiply a base number by itself. For example, 2<sup>3</sup> = 2 × 2 × 2 = 8. Negative exponents give reciprocals, and fractional exponents represent roots.</p>}
      formulaNote="bⁿ = b × b × … (n times)   •  b⁻ⁿ = 1 / bⁿ   •  b^(1/n) = ⁿ√b"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-5">
          <SliderInput label="Base (b)" value={base} onChange={setBase} min={-100} max={100} step={0.1} />
          <SliderInput label="Exponent (n)" value={exp} onChange={setExp} min={-20} max={50} step={0.5} />
        </div>

        <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Result</div>
          <div className="font-display text-lg text-bharat-navy mt-1">{base}<sup className="text-sm">{exp}</sup> =</div>
          <div className="font-display text-2xl md:text-3xl font-bold text-saffron-700 display-font mt-1 break-all">{scientific}</div>
          <div className="mt-5 space-y-2 text-sm">
            <Row label="Reciprocal" value={isFinite(1 / result) ? (1 / result).toExponential(4) : '—'} />
            <Row label="Square root of result" value={result >= 0 ? Math.sqrt(result).toFixed(6) : 'N/A'} />
            <Row label="Natural log (ln)" value={result > 0 ? Math.log(result).toFixed(6) : 'N/A'} />
          </div>
        </div>
      </div>
    </CalculatorLayout>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
    <span className="text-slate-600">{label}</span>
    <span className="display-font font-semibold text-bharat-navy">{value}</span>
  </div>
);

export default ExponentCalculator;
